import { fcApis } from '@apis/next/fcApis';
import { routePlanSimulatorApis } from '@apis/next/routePlanSimulatorApis';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { priceSimulatorStrings } from '@data/priceSimulator';
import { useMutation } from '@hooks/useMutation';
import {
  BookingInquiryCargosByPickupAddress,
  BookingInquiryCargosByPort,
} from '@models/next/BookingInquiry';
import { BookingInquiryContact } from '@models/next/Contact';
import { InvoiceHelper } from '@models/next/InvoiceHelper';
import { PriceSimulatorForm } from '@models/next/PriceSimulator';
import { RoutePlanSimulator, PostRoutePlanSimulator } from '@models/next/RoutePlanSimulator';
import { useQueryClient } from '@tanstack/react-query';
import { sanitizeZipCode } from '@utils/sanitize';
import dayjs from 'dayjs';
import { Either } from 'fp-ts/lib/Either';
import * as R from 'remeda';

export interface PriceSimulatorResolveRoutePlanSimulatorErrors {
  receiver?: {
    zip_code?: string;
  };
  pickup_addresses?: {
    zip_code?: string;
  }[];
  truck_type?: string;
  message?: string;
  cargos_port_name?: string;
}

const tryOrNull = async <T>(f: () => Promise<T>): Promise<T | null> => {
  try {
    return await f();
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const usePriceSimulatorResolveRoutePlanSimulators = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (
      form: PriceSimulatorForm & { owner_id: number },
    ): Promise<Either<PriceSimulatorResolveRoutePlanSimulatorErrors, RoutePlanSimulator[]>> => {
      const errors: PriceSimulatorResolveRoutePlanSimulatorErrors = {};

      // Validation of all zipcodes
      await Promise.all([
        (async () => {
          if (!form.receiver.zip_code) return;

          const receiverZipcodeResponse = await tryOrNull(() =>
            fcApis.client.get('/fc/pref-and-city-by-zipcode', {
              queries: { zipcode: sanitizeZipCode(form.receiver.zip_code) },
            }),
          );

          if (!receiverZipcodeResponse) {
            errors.receiver ||= {};
            errors.receiver!.zip_code = bookingInquiryStrings.receiverWrongZipcode;
          }
        })(),
        (async () => {
          if (!form.has_pickups) return;

          const pickupZipcodes = R.map(
            R.uniqBy(form.pickup_addresses, (p) => p.zip_code),
            (p) => p.zip_code,
          );

          const pickupZipcodeResponses = await Promise.all(
            pickupZipcodes.map((zipcode) =>
              tryOrNull(() =>
                fcApis.client.get('/fc/prov-and-city-by-zipcode', {
                  queries: { zipcode: sanitizeZipCode(zipcode) },
                }),
              ),
            ),
          );

          if (pickupZipcodeResponses.some((r) => !r)) {
            errors.pickup_addresses ||= [];

            const invalidPickupZipcodes = pickupZipcodeResponses.flatMap((r, i) =>
              r ? [] : [pickupZipcodes[i]],
            );

            for (let i = 0; i < form.pickup_addresses.length; i++) {
              const pickup = form.pickup_addresses[i];
              if (invalidPickupZipcodes.includes(pickup.zip_code)) {
                errors.pickup_addresses[i] = {
                  zip_code: bookingInquiryStrings.pickupAddressWrongZipcode,
                };
              }
            }
          }
        })(),
      ]);

      if (Object.keys(errors).length) return { _tag: 'Left', left: errors };

      // TODO: use zod method to compute price
      const getRoutePlanSimulatorPrice = (r: RoutePlanSimulator) => {
        return R.sumBy(
          r.simulator.invoice_details,
          (c) =>
            InvoiceHelper.getInvoiceDetailsComputed(c).totalPriceForCurrency(
              c.exchange_base_rate,
              'JPY',
            ) ?? 0,
        );
      };

      const resolveCargosByPickupAddress = (): BookingInquiryCargosByPickupAddress => {
        const result: BookingInquiryCargosByPickupAddress = [];

        for (let i = 0; i < form.pickup_addresses.length; i++) {
          const pickupAddress = form.pickup_addresses[i];
          const fakePickupAddress: BookingInquiryContact = {
            company_name: 'Simulator Company Ltd.',
            english_name: 'Simulator Company Ltd.',
            zip_code: pickupAddress.zip_code,
            address1: pickupAddress.city_name,
            address2: '909 9009th Simulator Rd.',
            address1_cn: '',
            email: 'simulator@d2dasia.com',
            phone: '+861234566789',
            principal_name: 'Simulator Sim',
            sns_id: 'wechat:simulator',
            social_credit_id: '',
            user: undefined,
          };

          const cargos = form.cargos.filter((c) => c.pickup_address_index - 1 === i);

          if (cargos.length > 0) {
            result.push({
              pickup_address: fakePickupAddress,
              cargos,
            });
          }
        }

        return result;
      };

      const resolveCargosByPort = (): BookingInquiryCargosByPort => {
        return [
          {
            port_name: form.cargos_port_name,
            cargos: form.cargos,
          },
        ];
      };

      const postRoutePlanSimulator: PostRoutePlanSimulator = {
        pickup_date: form.has_pickups ? dayjs().format('YYYY-MM-DD') : undefined,
        move_in_date: form.has_pickups ? undefined : dayjs().format('YYYY-MM-DD'),
        zipcode: form.receiver.zip_code,
        cargos: form.has_pickups
          ? {
              cargos_by_pickup_address: resolveCargosByPickupAddress(),
            }
          : {
              cargos_by_port: resolveCargosByPort(),
            },
        owner_id: form.owner_id,
        use_trading_agency: 'UNUSED',
        trading_agency_payer: 'jp',
        trade_condition: 'EXW',
        city_cd: form.receiver.city_cd || undefined,
        truck_type: form.truck_type === 'any' ? undefined : form.truck_type,
      };

      let routePlanSimulators = await routePlanSimulatorApis.client.post(
        '/route-plan-simulator',
        postRoutePlanSimulator,
        {
          queries: { type: 'PRICE_SIMULATOR' },
        },
      );

      /**
       * Might get repeated plans with same prices
       */
      routePlanSimulators = R.uniqBy(routePlanSimulators, (r) => r.route_plan.plan_id);

      if (routePlanSimulators.length === 0) {
        errors.message = priceSimulatorStrings.noRoutes;
        return { _tag: 'Left', left: errors };
      }

      if (!form.has_pickups) {
        routePlanSimulators = routePlanSimulators.filter(
          (r) => r.route_plan.receiving_city === form.cargos_port_name,
        );
      }

      if (routePlanSimulators.length === 0) {
        errors.cargos_port_name = priceSimulatorStrings.invalidCargosPortName;
        return { _tag: 'Left', left: errors };
      }

      let targetRoutePlanSimulator = R.minBy(routePlanSimulators, getRoutePlanSimulatorPrice)!;

      let right = [targetRoutePlanSimulator!];

      if (targetRoutePlanSimulator.route_plan.receiving_city === 'GUANGDONG') {
        right = R.sortBy(
          routePlanSimulators.filter(
            (r) =>
              r?.route_plan.receiving_city === 'GUANGDONG' &&
              r?.route_plan.pod === targetRoutePlanSimulator?.route_plan.pod,
          ),
          getRoutePlanSimulatorPrice,
        ).slice(0, 2);
      }

      return { _tag: 'Right', right };
    },
    {
      onSettled() {
        queryClient.invalidateQueries(
          routePlanSimulatorApis.getKeyByPath('get', '/route-plan-simulator'),
        );
      },
    },
  );
};
